import leonCampingCover from '../assets/images/na-leon-v-fortuna.webp';
import passportWeddingCover from '../assets/images/front-logo-passport-wedding.webp';

const STANDARD_PRICE_LABEL = 'P800';
const customInvitationDesignFiles = import.meta.glob('../pages/invitations/designs/*.jsx');

const hiddenDesignSlugs = new Set([
  'leon-camping',
  'lhyanne-debut',
  'passport-wedding',
]);

const completedInvitationMetadata = {
  'tiara-and-roberto-wedding': {
    order: 1,
    title: 'Tiara & Roberto Wedding',
    category: 'wedding',
    categoryLabel: 'Wedding',
    image: '/og/tiara-and-roberto-wedding.jpg',
    description: 'Completed blush and botanical wedding invitation for Tiara and Roberto.',
  },
  'fernandez-atizado-nuptial-wedding': {
    order: 2,
    title: 'Deniel & Merica Wedding',
    category: 'wedding',
    categoryLabel: 'Wedding',
    image: '/og/fernandez-atizado-nuptial-wedding.jpg',
    description: 'Completed nuptial wedding invitation with ceremony, reception, and RSVP flow.',
  },
  'stephen-jan-valerie-rose-wedding': {
    order: 3,
    title: 'Stephen Jan & Valerie Rose Wedding',
    category: 'wedding',
    categoryLabel: 'Wedding',
    image: '/og/stephen-jan-valerie-rose-wedding.png',
    description: 'Completed red floral wedding invitation with guest details and event guide.',
  },
  'merizhelle-kate-c-escondo-marvic-m-aranza-wedding': {
    order: 4,
    title: 'Marvic & Merizhelle Kate Wedding',
    category: 'wedding',
    categoryLabel: 'Wedding',
    image: '/og/merizhelle-kate-c-escondo-marvic-m-aranza-wedding.jpg',
    description: 'Completed embroidered garden wedding invitation with RSVP and guest reminders.',
  },
  'passport-wedding-john-kristenz': {
    order: 5,
    title: 'John & Kristenz Passport Wedding',
    category: 'wedding',
    categoryLabel: 'Wedding',
    image: passportWeddingCover,
    description: 'Completed passport-style wedding invitation with an interactive flipbook experience.',
  },
  'angel-chris-jeralyn-wedding': {
    order: 6,
    title: 'Angel Chris & Jeralyn Wedding',
    category: 'wedding',
    categoryLabel: 'Wedding',
    image: '/og/angel-chris-jeralyn-wedding.jpg',
    description: 'Completed burgundy and ivory wedding invitation with flipbook pages, guest details, RSVP, and venue guide.',
  },
  'dominick-jhay-ruzol-pareja-birthday': {
    order: 7,
    title: "Dominick Jhay's 7th Birthday",
    category: 'birthday',
    categoryLabel: 'Birthday',
    image: '/og/dominick-jhay-ruzol-pareja-birthday.jpg',
    description: 'Completed Roblox birthday invitation with RSVP, celebration details, and party guide.',
  },
  'claudia-eve-polidario-7th-birthday': {
    order: 8,
    title: "Claudia Eve's 7th Birthday",
    category: 'birthday',
    categoryLabel: 'Birthday',
    image: '/og/claudia-eve-polidario-7th-birthday.jpg',
    description: 'Completed pastel rainbow birthday invitation with photos, program, and RSVP flow.',
  },
  'jan-kadence-birthday-and-christening': {
    order: 9,
    title: "Jan Kadence's Birthday & Christening",
    category: 'birthday',
    categories: ['birthday', 'christening'],
    categoryLabel: 'Birthday & Christening',
    image: '/og/jan-kadence-birthday-and-christening.jpg',
    description: 'Completed basketball-themed birthday and christening invitation with two event venues.',
  },
  'camping-na': {
    order: 10,
    title: "N.A. Leon's Birthday & Christening",
    category: 'christening',
    categories: ['birthday', 'christening'],
    categoryLabel: 'Birthday & Christening',
    image: leonCampingCover,
    description: 'Completed camping-themed birthday and christening invitation with flipbook details.',
  },
  'sofia-alexandra-debut': {
    order: 11,
    title: "Sofia Alexandra's Debut",
    category: 'birthday',
    categoryLabel: 'Debut',
    image: '/og/sofia-alexandra-debut.jpg',
    description: 'Completed enchanted forest debut invitation with program, gallery, and event details.',
  },
  'debut-lhyanne-frankie': {
    order: 12,
    title: "Lhyanne Frankie's Debut",
    category: 'birthday',
    categoryLabel: 'Debut',
    image: '/og/lhyanne-frankie.jpg',
    description: 'Completed ethereal debut invitation with traditions, gallery, and RSVP details.',
  },
  'birthday-jenine-charisse': {
    order: 13,
    title: "Jenine Charisse's Debut",
    category: 'birthday',
    categoryLabel: 'Debut',
    image: '/og/jenine-charisse.jpg',
    description: 'Completed 18th birthday debut invitation with program flow, gallery, and venue details.',
  },
  'frances-maurice-gwyneth-b-maglalang-debut': {
    order: 14,
    title: "Frances Gwyneth's Hollywood Debut",
    category: 'birthday',
    categoryLabel: 'Debut',
    image: '/og/frances-maurice-gwyneth-b-maglalang-debut.jpg',
    description: 'Completed Hollywood-themed debut invitation with red & black palette, traditions, venue, and RSVP.',
  },
};

function normalizeInvitationId(value) {
  return String(value || '')
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replace(/[_\s]+/g, '-')
    .replace(/[^a-zA-Z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
}

function getSlugFromDesignPath(filePath) {
  const fileName = filePath.split('/').pop()?.replace(/\.jsx$/, '') || '';
  return normalizeInvitationId(fileName);
}

function titleFromSlug(slug) {
  return slug
    .split('-')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function inferCategoryFromSlug(slug) {
  if (/wedding|nuptial|passport/.test(slug)) return 'wedding';
  if (/christening|baptism|camping/.test(slug)) return 'christening';
  if (/corporate|business|seminar/.test(slug)) return 'corporate';
  if (/anniversary/.test(slug)) return 'anniversary';
  return 'birthday';
}

function createInvitationTemplate(slug, index) {
  const metadata = completedInvitationMetadata[slug] || {};
  const category = metadata.category || inferCategoryFromSlug(slug);

  return {
    id: slug,
    title: metadata.title || titleFromSlug(slug),
    category,
    categories: metadata.categories || [category],
    categoryLabel: metadata.categoryLabel || getInvitationCategory(category)?.label || titleFromSlug(category),
    slug,
    image: metadata.image,
    description: metadata.description || 'Completed custom digital invitation from the design gallery.',
    price: metadata.price || STANDARD_PRICE_LABEL,
    previewPath: `/invitations/custom/${slug}`,
    order: metadata.order ?? index + 1,
  };
}

export const invitationCategories = [
  {
    value: 'all',
    icon: null,
    label: 'All Designs',
    path: '/invitations',
    headline: 'All digital invitation designs',
    blurb:
      'Browse polished design samples for weddings, birthdays, christenings, corporate events, and anniversaries.',
  },
  {
    value: 'wedding',
    icon: null,
    label: 'Wedding',
    path: '/wedding',
    headline: 'Wedding digital invitations',
    blurb:
      'Elegant RSVP-ready designs for save-the-dates, ceremonies, receptions, and intimate wedding celebrations.',
  },
  {
    value: 'birthday',
    icon: null,
    label: 'Birthday',
    path: '/birthday',
    headline: 'Birthday digital invitations',
    blurb:
      'Playful and polished online invitations for kids parties, milestone birthdays, and themed celebrations.',
  },
  {
    value: 'christening',
    icon: null,
    label: 'Christening',
    path: '/christening',
    headline: 'Christening digital invitations',
    blurb:
      'Meaningful baptism and christening invitation designs that are easy for family and sponsors to view on mobile.',
  },
  {
    value: 'corporate',
    icon: null,
    label: 'Corporate',
    path: '/corporate',
    headline: 'Corporate digital invitations',
    blurb:
      'Professional online invitations for seminars, launches, company parties, and executive events.',
  },
  {
    value: 'anniversary',
    icon: null,
    label: 'Anniversary',
    path: '/anniversary',
    headline: 'Anniversary digital invitations',
    blurb:
      'Elegant online invitation designs for silver, golden, and milestone anniversary celebrations.',
  },
];

const categoryPageContent = {
  wedding: {
    eyebrow: 'Wedding digital invitations',
    heading: 'Wedding Digital Invitations for Modern Filipino Celebrations',
    description:
      'Explore elegant wedding digital invitations that are easy to share, mobile-friendly for guests, and ready for RSVP collection.',
    lead:
      'Choose a wedding design that feels polished online and practical for real guest communication. Each sample can be personalized with your ceremony, reception, and save-the-date details.',
    highlights: [
      'Designed for Messenger, Facebook, email, and mobile sharing',
      'Ideal for save-the-dates, ceremony invites, and reception reminders',
      'Personalized around your motif, event details, RSVP flow, and guest information',
    ],
    metrics: [
      { label: 'Standard package', value: 'P800' },
      { label: 'Turnaround', value: '2-3 days' },
      { label: 'Best for', value: 'Ceremony and reception invites' },
    ],
    designLead:
      'Compare the wedding invitation designs below and choose the style direction that matches your ceremony, reception, or save-the-date.',
    ctaLabel: 'Book a Wedding Invitation',
  },
  birthday: {
    eyebrow: 'Birthday digital invitations',
    heading: 'Birthday Digital Invitations for Kids and Milestone Celebrations',
    description:
      'Browse birthday digital invitations for kids parties, themed celebrations, and milestone birthdays that need a fast, shareable online invite.',
    lead:
      'Birthday invitations usually need quick turnaround and easy guest sharing. These design samples help you choose a playful, elegant, or themed direction before booking.',
    highlights: [
      'Works well for kids birthdays, 7th birthdays, debuts, and milestone events',
      'Simple to share with family groups and parent circles on mobile',
      'A practical option when you want a themed invitation without a long wait',
    ],
    metrics: [
      { label: 'Standard package', value: 'P800' },
      { label: 'Turnaround', value: '2-3 days' },
      { label: 'Best for', value: 'Kids parties and milestone birthdays' },
    ],
    designLead:
      'Review the birthday invitation designs below to find the best fit for playful themes, elegant milestones, and fiesta-inspired parties.',
    ctaLabel: 'Book a Birthday Invitation',
  },
  christening: {
    eyebrow: 'Christening digital invitations',
    heading: 'Christening and Baptism Digital Invitations for Family Celebrations',
    description:
      'Choose christening digital invitations that make it easy to share ceremony details, reception information, and RSVP instructions with family and sponsors.',
    lead:
      'For baptisms and christenings, clarity matters as much as design. These samples help families plan meaningful invitation pages that guests can open on any phone.',
    highlights: [
      'Made for baptism, christening, and blessing celebrations',
      'Clear layout for church, reception, and RSVP details',
      'Soft, elegant styles for faith-centered family events',
    ],
    metrics: [
      { label: 'Standard package', value: 'P800' },
      { label: 'Turnaround', value: '2-3 days' },
      { label: 'Best for', value: 'Baptisms and christenings' },
    ],
    designLead:
      'Browse the christening invitation designs below for elegant baptism and faith-focused family celebrations.',
    ctaLabel: 'Book a Christening Invitation',
  },
  corporate: {
    eyebrow: 'Corporate digital invitations',
    heading: 'Corporate Digital Invitations for Professional Events and Launches',
    description:
      'Browse corporate digital invitations for seminars, launches, networking events, and company gatherings that need a cleaner and more professional online presence.',
    lead:
      'Corporate invitations need to look organized, modern, and easy to open on any device. These design samples help teams choose a branded event direction quickly.',
    highlights: [
      'Suitable for seminars, launches, trainings, and executive events',
      'Professional layouts that keep key event details clear',
      'A faster option for teams that need a presentable invite quickly',
    ],
    metrics: [
      { label: 'Standard package', value: 'P800' },
      { label: 'Turnaround', value: '2-3 days' },
      { label: 'Best for', value: 'Seminars, launches, and company events' },
    ],
    designLead:
      'Explore the corporate invitation designs below for business events that need a polished, mobile-ready invitation page.',
    ctaLabel: 'Book a Corporate Invitation',
  },
  anniversary: {
    eyebrow: 'Anniversary digital invitations',
    heading: 'Anniversary Digital Invitations for Milestone Love Stories',
    description:
      'Explore anniversary digital invitations for silver, golden, and milestone celebrations that deserve an elegant online invitation experience.',
    lead:
      'Anniversary celebrations often need a softer, more elegant presentation. These design samples help couples and families share details in a format guests can open instantly.',
    highlights: [
      'Built for silver, golden, and milestone anniversary events',
      'Elegant layouts that feel celebratory without being hard to navigate',
      'A simple way to share event details with family across devices',
    ],
    metrics: [
      { label: 'Standard package', value: 'P800' },
      { label: 'Turnaround', value: '2-3 days' },
      { label: 'Best for', value: 'Silver and golden anniversaries' },
    ],
    designLead:
      'Compare the anniversary invitation designs below to find a style that fits elegant milestone celebrations.',
    ctaLabel: 'Book an Anniversary Invitation',
  },
};

/**
 * Enrich a DB invitation row with metadata (image, description, etc.)
 * @param {Object} dbRow - Raw invitation from the database
 * @returns {Object} Enriched invitation template
 */
export function enrichInvitationFromDB(dbRow) {
  const slug = normalizeInvitationId(dbRow.invitationId || '');
  const metadata = completedInvitationMetadata[slug] || {};
  const category = dbRow.category || metadata.category || inferCategoryFromSlug(slug);

  return {
    id: slug,
    invitationId: dbRow.invitationId,
    title: dbRow.title || metadata.title || titleFromSlug(slug),
    category,
    categories: metadata.categories || [category],
    categoryLabel: dbRow.categoryLabel || metadata.categoryLabel || getInvitationCategory(category)?.label || titleFromSlug(category),
    slug,
    image: metadata.image || `/og/${slug}.jpg`,
    description: metadata.description || `Completed ${category} invitation with RSVP tracking and event details.`,
    price: metadata.price || STANDARD_PRICE_LABEL,
    previewPath: dbRow.previewPath || `/invitations/custom/${slug}`,
    eventDate: dbRow.eventDate,
    order: metadata.order ?? 999,
  };
}

export const invitationTemplates = Object.keys(customInvitationDesignFiles)
  .map(getSlugFromDesignPath)
  .filter((slug) => slug && !hiddenDesignSlugs.has(slug))
  .filter((slug, index, slugs) => slugs.indexOf(slug) === index)
  .map(createInvitationTemplate)
  .sort((first, second) => first.order - second.order || first.title.localeCompare(second.title));

export function getInvitationCategory(value) {
  return invitationCategories.find((category) => category.value === value) || null;
}

export function getCategoryPageContent(value) {
  return categoryPageContent[value] || null;
}

export function getTemplatesByCategory(value) {
  if (!value || value === 'all') return invitationTemplates;
  return invitationTemplates.filter((template) => (
    template.category === value || template.categories?.includes(value)
  ));
}

export function getVisibleInvitationCategories() {
  return invitationCategories.filter((category) => category.value !== 'all');
}

const categoryExtras = {
  wedding: {
    testimonials: [
      {
        name: 'Maria & Juan Santos',
        event: 'Wedding Reception, March 2026',
        text: 'The digital invitation was perfect for our wedding! Our guests loved how easy it was to RSVP and get all the event details on their phones. Highly recommend!',
      },
      {
        name: 'Kristine & Mark Reyes',
        event: 'Church Wedding, January 2026',
        text: 'Beautiful design and super convenient. We saved so much time and money compared to printed invites. The team was very responsive to our requests.',
      },
      {
        name: 'Ana & Carlo Dela Cruz',
        event: 'Garden Wedding, December 2025',
        text: 'Our guests were impressed with how professional and elegant the invitation looked. The RSVP tracking feature made planning so much easier!',
      },
    ],
    faqs: [
      {
        question: 'How long does it take to create a wedding digital invitation?',
        answer: 'Standard turnaround is 2-3 days from the time we receive all your details and confirmation. Rush orders can be completed in 24 hours for an additional fee.',
      },
      {
        question: 'Can I customize the design to match our wedding theme?',
        answer: 'Absolutely! All our wedding invitations can be personalized with your chosen colors, motif, photos, event details, and RSVP preferences.',
      },
      {
        question: 'How do guests RSVP through the digital invitation?',
        answer: 'Guests can RSVP directly through the invitation link. They fill out a simple form with their name, attendance confirmation, and number of guests. You receive all responses in real-time.',
      },
      {
        question: 'Can we include multiple events (ceremony, reception, etc.)?',
        answer: 'Yes! We can include separate sections for your church ceremony, reception venue, pre-wedding events, and any other celebrations you want to share.',
      },
      {
        question: 'What information should we prepare before booking?',
        answer: 'Have ready: couple names, event date(s), venue details, event timeline, dress code, special instructions, and any photos you want included. We\'ll guide you through everything during the booking process.',
      },
      {
        question: 'How do we share the invitation with our guests?',
        answer: 'You\'ll receive a shareable link that works on any device. Send it via Messenger, email, text message, or post it on social media. It\'s mobile-friendly and works without any app installation.',
      },
    ],
  },
};

export function getCategoryExtras(value) {
  return categoryExtras[value] || null;
}
